import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, Plus, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCart } from '../context/CartContext';
import axios from 'axios';

const WHATSAPP_NUMBER = '919074305418';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POOJA_IMAGES = {
  'thilahomam': 'https://images.pexels.com/photos/12430842/pexels-photo-12430842.jpeg?auto=compress&cs=tinysrgb&w=600',
  'ganapathi-homam': 'https://customer-assets-4nw71qhi.emergentagent.net/job_pooja-booking-8/artifacts/6f71a11777e581c7_ganpathy-homam-2.jpg',
  'archana': 'https://customer-assets-4nw71qhi.emergentagent.net/job_pooja-booking-8/artifacts/cd08d18468fc3c20_archana.jpeg'
};

const POOJA_DESCRIPTIONS = {
  'thilahomam': 'Sacred fire ritual for the departed souls. Performed at Parashurama Temple for moksha and peace of ancestors.',
  'ganapathi-homam': 'Fire ritual dedicated to Lord Ganesha for removing obstacles, new beginnings, and divine blessings.',
  'archana': 'Sacred offering to the deity with the chanting of divine names. A simple and powerful form of worship.'
};

const POOJA_COLORS = {
  'thilahomam': '#C2410C', 'ganapathi-homam': '#B45309', 'archana': '#9A3412'
};
const DEFAULT_COLOR = '#92400E';
const DEFAULT_IMAGE = 'https://images.pexels.com/photos/5553622/pexels-photo-5553622.jpeg?auto=compress&cs=tinysrgb&w=600';

export default function HomePage() {
  const navigate = useNavigate();
  const { addToCart, removeFromCart, isInCart, cartCount } = useCart();
  const [poojas, setPoojas] = useState([]);

  useEffect(() => {
    axios.get(`${API}/settings`).then(res => {
      const data = res.data.poojas || {};
      const cards = Object.entries(data).map(([id, p]) => {
        const baseTotal = (p.temple_fee || 0) + (p.convenience_fee || 0) + (p.has_dakshina ? (p.default_dakshina || 0) : 0);
        return {
          id,
          title: (p.title || id).replace(' Booking', ''),
          titleMl: p.title_ml || '',
          description: POOJA_DESCRIPTIONS[id] || `Book ${p.title || id} pooja online with Prayan Pilgrim Services.`,
          price: `₹${p.temple_fee || 0}`,
          image: POOJA_IMAGES[id] || DEFAULT_IMAGE,
          color: POOJA_COLORS[id] || DEFAULT_COLOR
        };
      });
      setPoojas(cards);
    }).catch(() => {});
  }, []);

  const toggleCart = (poojaId) => {
    if (isInCart(poojaId)) removeFromCart(poojaId);
    else addToCart(poojaId);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAF9F6' }}>
      {/* Floating WhatsApp Button */}
      <a href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Hi, I need help with pooja booking')}`} target="_blank" rel="noopener noreferrer"
        className="fixed top-20 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
        style={{ backgroundColor: '#25D366', color: 'white', animation: 'pulse 2s ease-in-out infinite' }} data-testid="whatsapp-float-button">
        <svg viewBox="0 0 32 32" className="w-6 h-6" fill="currentColor"><path d="M16 0C7.164 0 0 7.164 0 16c0 2.834.745 5.496 2.047 7.807L0 32l8.358-2.19A15.93 15.93 0 0 0 16 32c8.836 0 16-7.164 16-16S24.836 0 16 0zm9.489 22.54c-.391.977-1.953 1.793-2.828 1.903-.75.094-1.723.133-2.781-.174-.641-.187-1.469-.438-2.523-.86-4.406-1.766-7.281-6.188-7.5-6.469-.219-.281-1.781-2.375-1.781-4.531 0-2.156 1.125-3.219 1.547-3.656.359-.375.984-.562 1.562-.562.187 0 .359.016.516.031.422.016.641.031.922.719.281.703.969 2.359 1.047 2.531.094.172.156.375.031.594-.125.234-.187.375-.375.594-.187.219-.375.469-.531.641-.172.156-.359.328-.156.641.203.297.906 1.5 1.953 2.438 1.344 1.188 2.469 1.563 2.828 1.734.281.141.609.109.812-.109.266-.281.594-.75.922-1.219.234-.344.531-.391.859-.25.344.125 2.172 1.031 2.547 1.219.375.187.625.281.719.438.094.156.094.906-.297 1.875z"/></svg>
        <span className="font-semibold text-sm whitespace-nowrap">Chat on WhatsApp</span>
      </a>

      {/* Hero Header */}
      <div className="relative h-[40vh] min-h-[300px] overflow-hidden">
        <img src="https://images.pexels.com/photos/12430842/pexels-photo-12430842.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Temple" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#FAF9F6]"></div>
        <div className="relative h-full flex flex-col items-center justify-center px-4 space-y-3">
          <div className="px-6 sm:px-10 py-3 sm:py-4 rounded-2xl" style={{ backgroundColor: 'rgba(194, 65, 12, 0.95)', boxShadow: '0 8px 32px rgba(194, 65, 12, 0.3)' }}>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight font-bold text-white text-center whitespace-nowrap" style={{ fontFamily: '"Cormorant Garamond", serif' }} data-testid="main-heading">Prayan Pilgrim Services</h1>
          </div>
          <p className="text-white/90 text-base sm:text-lg text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>Kerala's Trusted Pooja Booking Service</p>
        </div>
      </div>

      {/* Video Section */}
      <div className="max-w-3xl mx-auto px-4 -mt-12 mb-8" style={{ position: 'relative', zIndex: 15 }}>
        <div className="bg-white shadow-2xl rounded-3xl overflow-hidden" style={{ boxShadow: '0 25px 50px -12px rgba(120, 113, 108, 0.15)' }} data-testid="video-section">
          <div className="p-5 sm:p-8 space-y-4">
            <h2 className="text-xl sm:text-2xl font-bold text-center" style={{ color: '#1C1917', fontFamily: '"Cormorant Garamond", serif' }}>What is Thilahomam ?</h2>
            <div className="flex justify-center">
              <div className="rounded-2xl overflow-hidden shadow-lg" style={{ maxWidth: '320px', width: '100%' }}>
                <video controls playsInline preload="metadata" className="w-full" style={{ aspectRatio: '9/16', objectFit: 'cover', backgroundColor: '#000' }} data-testid="thilahomam-video">
                  <source src="https://customer-assets.emergentagent.com/job_pooja-booking-8/artifacts/nt3c61tf_1001898119_216x384.mp4" type="video/mp4" />
                </video>
              </div>
            </div>
            <div className="text-center p-4 rounded-xl border-2" style={{ backgroundColor: '#FEF3C7', borderColor: '#FCD34D' }} data-testid="video-speaker-info">
              <p className="text-lg font-bold" style={{ color: '#78350F' }}>Praveen Sharma</p>
              <p className="text-base font-semibold" style={{ color: '#92400E' }}>Poojari in Parashurama Temple</p>
            </div>
          </div>
        </div>
      </div>

      {/* Pooja Cards */}
      <div className="max-w-5xl mx-auto px-4 pb-28">
        <h2 className="text-2xl sm:text-3xl font-bold text-center mb-8" style={{ color: '#1C1917', fontFamily: '"Cormorant Garamond", serif' }}>Our Pooja Services</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {poojas.map((pooja) => {
            const inCart = isInCart(pooja.id);
            return (
              <div key={pooja.id} className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col" data-testid={`pooja-card-${pooja.id}`}>
                <div className="relative h-48 overflow-hidden">
                  <img src={pooja.image} alt={pooja.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-3 left-4">
                    <h3 className="text-white text-xl font-bold">{pooja.title}</h3>
                    <p className="text-white/80 text-sm">{pooja.titleMl}</p>
                  </div>
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <p className="text-sm leading-relaxed flex-1 mb-4" style={{ color: '#57534E' }}>{pooja.description}</p>
                  <div className="space-y-2.5">
                    <div className="flex items-center justify-between mb-1">
                      <div>
                        <span className="text-lg font-bold" style={{ color: pooja.color }}>{pooja.price}</span>
                        <span className="text-xs ml-1" style={{ color: '#A8A29E' }}>temple fee</span>
                      </div>
                    </div>
                    <Button onClick={() => navigate(`/book/${pooja.id}`)} className="w-full py-3 rounded-xl font-semibold text-white text-base" style={{ backgroundColor: pooja.color }} data-testid={`book-${pooja.id}-btn`}>Book Now</Button>
                    <button
                      onClick={() => toggleCart(pooja.id)}
                      className={`w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-all duration-200 ${inCart ? 'text-white' : 'hover:opacity-90'}`}
                      style={inCart ? { backgroundColor: pooja.color + 'CC' } : { color: pooja.color, backgroundColor: 'transparent', border: `1px solid ${pooja.color}40` }}
                      data-testid={`cart-toggle-${pooja.id}`}
                    >
                      {inCart ? <><Check className="h-3 w-3" /> Added</> : <><Plus className="h-3 w-3" /> Add to Cart</>}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Floating Cart Bar */}
      {cartCount > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-50" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.6) 100%)', backdropFilter: 'blur(12px)' }}>
          <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <ShoppingCart className="h-6 w-6 text-white" />
                <span className="absolute -top-2 -right-2 bg-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center" style={{ color: '#C2410C' }} data-testid="cart-count-badge">{cartCount}</span>
              </div>
              <span className="text-white text-sm font-medium">{cartCount} pooja{cartCount > 1 ? 's' : ''} in cart</span>
            </div>
            <Button onClick={() => navigate('/cart')} className="px-6 py-2.5 rounded-xl font-bold text-white shadow-lg" style={{ backgroundColor: '#C2410C' }} data-testid="proceed-to-cart-btn">
              Proceed to Booking
            </Button>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="border-t" style={{ borderColor: '#E7E5E4', backgroundColor: '#F5F5F4' }}>
        <div className="max-w-3xl mx-auto px-4 py-8 text-center space-y-3">
          <p className="text-xs leading-relaxed" style={{ color: '#78716C' }}>Prayan Pilgrim Services is an independent private service and is not the official website or representative of Sree Parashurama Temple or its Devaswom. All bookings and services are subject to the rules, timings, availability, and decisions of the Temple authorities.</p>
        </div>
      </div>
    </div>
  );
}
